import { CHAT_PATH, type ApiClient } from '@aithora/shared';

export interface ChatReply {
  /** Atlas's words, every screened segment joined. */
  text: string;
  /** The conversation the turn was stored under (absent when the turn was not kept). */
  conversationId: string | null;
}

/** Why a /chat call did not answer, in words a person can act on. */
export class ChatRefused extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message);
  }
}

/**
 * POST /chat, Atlas itself (src/aithora/router.ts). It answers as a stream:
 * `data: {"text": "..."}` frames, sentence by sentence (each already screened), then
 * `data: [DONE]`. Failures answer JSON with a status (declined, consent, quota, rate).
 */
export async function sendChat(
  client: ApiClient,
  body: { message: string; userId: string; organizationId: string; conversationId?: string | null },
  onText?: (soFar: string) => void,
  signal?: AbortSignal,
): Promise<ChatReply> {
  const res = await client.raw(CHAT_PATH, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify({ ...body, conversationId: body.conversationId ?? undefined }),
    signal,
  });
  if (!res.ok || !res.body) {
    let d: { error?: string; message?: string; code?: string } = {};
    try {
      d = await res.json();
    } catch {
      /* not JSON */
    }
    const why =
      d.error === 'companion_declined'
        ? 'Atlas is turned off for this resident at their request.'
        : d.code === 'CONSENT_REQUIRED'
          ? 'Consent is needed before Atlas can be used.'
          : res.status === 429
            ? 'Too many messages just now. Try again in a minute.'
            : d.message || d.error || 'Atlas did not answer.';
    throw new ChatRefused(res.status, d.code ?? d.error ?? null, why);
  }
  const conversationId = res.headers.get('x-conversation-id');
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  let text = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, i);
      buf = buf.slice(i + 2);
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue;
        const data = line.slice(5).trim();
        if (data === '[DONE]') return { text: text.trim(), conversationId };
        try {
          const seg = JSON.parse(data) as { text?: string };
          if (seg.text) {
            text += (text && !/\s$/.test(text) ? ' ' : '') + seg.text;
            onText?.(text);
          }
        } catch {
          /* a frame that is not JSON is skipped */
        }
      }
    }
  }
  return { text: text.trim(), conversationId };
}
